import { Typography } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { createPickingList } from 'lib/api'
import { useOrderStore } from 'lib/hooks'
import { useFetchOrders } from 'lib/hooks/useFetchOrders'
import { useSnackbar } from 'notistack'
import { memo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getPaidOrdersId, Order } from 'shared'
import { SelectPickingListDialog } from '../SelectPickingListDialog'

export interface PickingListToolbarProps {
  toolNamesWidth: string
  handleUpdateOrders: (updatedOrders: Order[]) => void
}

export const PickingListToolbar = memo(
  ({ toolNamesWidth, handleUpdateOrders }: PickingListToolbarProps): JSX.Element => {
    const { t } = useTranslation()
    const { hasPermission } = useUser()

    const { orders, selectedOrderIds, setSelectedOrderIds, setSelectedOrders } = useOrderStore()
    const { convertOrderResponseDtoToOrderClass } = useFetchOrders()

    const [openSelectPickingListDialog, setOpenSelectPickingListDialog] = useState<boolean>(false)
    const [creatingPickingList, setCreatingPickingList] = useState<boolean>(false)
    const { enqueueSnackbar } = useSnackbar()

    const createAndShowPickingList = async (orderIds: number[]) => {
      try {
        const orders = await createPickingList(orderIds)
        const newOrders = orders.map((order) => convertOrderResponseDtoToOrderClass(order))
        handleUpdateOrders(newOrders)
      } catch (e: any) {
        console.error('createPickingList failed:', e)
        const detail = e?.response?.data?.error
        const fallback = t('orders.picking.errorCreatingPickingList')
        // ordersNotLinkable: some orders on screen are already in a list or no longer to pick
        const message =
          detail === 'ordersNotLinkable'
            ? t('orders.picking.ordersNotLinkable')
            : detail
            ? `${fallback}: ${detail}`
            : fallback
        enqueueSnackbar(message, { variant: 'error' })
      }
    }

    const handleCreateFromSelected = async () => {
      setCreatingPickingList(true)
      if (selectedOrderIds.length > 0) {
        await createAndShowPickingList(selectedOrderIds)
      } else {
        enqueueSnackbar(t('orders.picking.noOrdersToPick'), { variant: 'error' })
      }
      setSelectedOrderIds([])
      setSelectedOrders([])
      setCreatingPickingList(false)
    }

    const handleCloseSelectPickingListDialog = () => {
      setOpenSelectPickingListDialog(false)
    }

    const handleNewAutomaticList = async () => {
      // automatic list from all paid orders
      setCreatingPickingList(true)
      const paidOrdersId = getPaidOrdersId(orders)
      if (paidOrdersId.length > 0) {
        await createAndShowPickingList(paidOrdersId)
      } else {
        enqueueSnackbar(t('orders.picking.noOrdersToPick'), { variant: 'error' })
      }
      setCreatingPickingList(false)
    }

    const handleCreateLargeList = async () => {
      // automatic list from all paid orders
      setCreatingPickingList(true)
      let paidOrdersId = getPaidOrdersId(orders)
      paidOrdersId = paidOrdersId.slice(0, 70)
      if (paidOrdersId.length > 0) {
        await createAndShowPickingList(paidOrdersId)
      } else {
        enqueueSnackbar(t('orders.picking.noOrdersToPick'), { variant: 'error' })
      }
      setCreatingPickingList(false)
    }

    return (
      <>
        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 w-100">
          <Typography color="primary" style={{ minWidth: toolNamesWidth }}>
            {t('orders.picking.title')}
          </Typography>

          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="contained"
              color="primary"
              disabled={selectedOrderIds.length === 0 || creatingPickingList}
              onClick={handleCreateFromSelected}
              data-testid="create-picking-list-from-selected-btn"
            >
              {t('orders.picking.createFromSelected')}
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={creatingPickingList}
              onClick={handleNewAutomaticList}
              data-testid="create-picking-list-from-all-btn"
            >
              {t('orders.picking.createFromAll')}
            </Button>

            {hasPermission('use_internal_tools') && (
              <Button
                variant="contained"
                color="primary"
                disabled={creatingPickingList}
                onClick={handleCreateLargeList}
              >
                Test - Create large picking list
              </Button>
            )}
          </div>
        </div>

        <SelectPickingListDialog
          open={openSelectPickingListDialog}
          onClose={handleCloseSelectPickingListDialog}
        />
      </>
    )
  }
)
